/**
 * Tabs - Tab navigation component
 */

import React, { useState, useCallback, useLayoutEffect, useRef } from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  variant?: 'line' | 'pill';
  fullWidth?: boolean;
  ariaLabel?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'line',
  fullWidth = false,
  ariaLabel,
}) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const measureOverflow = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
    setIsScrolledToEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  // Measure on mount and on strip resize (before browser paint)
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;

    measureOverflow();

    const ro = new ResizeObserver(() => measureOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureOverflow]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
    setIsScrolledToEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  const showFade = isOverflowing && !isScrolledToEnd;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Filter to enabled tabs only — disabled tabs are not navigable
    const enabledTabs = tabs.filter((t) => !t.disabled);
    if (enabledTabs.length === 0) return;

    const currentEnabledIndex = enabledTabs.findIndex((t) => t.id === activeTab);
    // If active tab is disabled or unknown, treat as no-op for navigation
    if (currentEnabledIndex === -1) return;

    let nextIndex: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (currentEnabledIndex + 1) % enabledTabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = enabledTabs.length - 1;
        break;
      default:
        return; // Don't preventDefault for keys we don't handle
    }

    e.preventDefault();
    onChange(enabledTabs[nextIndex].id);
  };

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <div
        ref={stripRef}
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        className={`
          flex whitespace-nowrap
          ${fullWidth ? 'justify-between' : 'overflow-x-auto'}
          ${variant === 'line' ? 'border-b border-gray-200 dark:border-gray-700' : ''}
          ${variant === 'pill' ? 'gap-2' : 'gap-6'}
        `}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => !tab.disabled && onChange(tab.id)}
            disabled={tab.disabled}
            className={`
              whitespace-nowrap
              ${variant === 'line'
                ? `
                  pb-3 pt-1 border-b-2 -mb-px transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `
                : `
                  px-4 py-2 rounded-lg font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : tab.disabled
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }
                `
              }
              flex items-center gap-2 text-sm font-medium
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      <div
        data-testid="tabs-fade-right"
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-900 to-transparent transition-opacity ${showFade ? '' : 'opacity-0'}`}
      />
    </div>
  );
};

interface TabPanelProps {
  children: React.ReactNode;
  className?: string;
  tabId?: string; // when provided, enables full role="tabpanel" semantics
}

export const TabPanel: React.FC<TabPanelProps> = ({ children, className = '', tabId }) => {
  if (!tabId) {
    return <div className={className}>{children}</div>; // backwards compat
  }
  return (
    <div
      id={`panel-${tabId}`}
      role="tabpanel"
      aria-labelledby={`tab-${tabId}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
};
